import type { SpinPrize, SpinResult } from './api.js';

/**
 * Which wedge the pointer must stop on.
 *
 * The wheel used to find it with `prizes.findIndex(p => p.key === prizeKey)`,
 * which is only correct while every key on the wheel is unique. It was not:
 * nothing refused a config with two wedges carrying the same key, so the pointer
 * always stopped on the FIRST of them while the credit came from whichever one
 * the server drew — the player watched one prize land and was paid another
 * (#555). svc-promo refuses such a configuration now (PROMO-724), but wheels
 * saved before that guard existed are still stored and still spinning, so the
 * client may not rely on keys being unique.
 *
 * So the server says WHICH wedge by position (`prizeIndex`) and this trusts it,
 * with two ways back to the old behaviour that are both about the reply and the
 * table disagreeing, never about a duplicate key:
 *
 *   - no index at all — a server older than #555, or a history row, which never
 *     carried the table it was drawn from;
 *   - an index that is out of range, or points at a wedge whose key is not the
 *     one that won — the operator edited the wheel between the page load and the
 *     draw, so the position is an index into a table this screen no longer has.
 *
 * The fallback is the key match, and a key that matches nothing lands on 0 —
 * some wedge has to be under the pointer, and the result modal names the prize
 * regardless.
 */
export function landingIndex(
  prizes: ReadonlyArray<Pick<SpinPrize, 'key'>>,
  result: Pick<SpinResult, 'prizeKey' | 'prizeIndex'>,
): number {
  const index = result.prizeIndex;
  if (
    typeof index === 'number' &&
    Number.isInteger(index) &&
    index >= 0 &&
    index < prizes.length &&
    prizes[index]?.key === result.prizeKey
  ) {
    return index;
  }
  return Math.max(0, prizes.findIndex((p) => p.key === result.prizeKey));
}

/**
 * The rotation that brings wedge `index` under the top pointer.
 *
 * Only ever grows, so the always-forward transition animates in one direction;
 * five whole turns are the spin itself. Kept beside {@link landingIndex} so the
 * two halves of "where does the pointer stop" can be read, and asserted, in one
 * place.
 */
export function landingRotation(current: number, prizeCount: number, index: number): number {
  const slice = 360 / (prizeCount || 1);
  const base = current - (current % 360);
  return base + 360 * 5 + (360 - (index * slice + slice / 2));
}
